import { logger } from '@lib/logger';
import { isAxiosError } from 'axios';
import type { App } from 'vue';

/**
 * Global error capture.
 *
 * Until this module existed, only errors we had explicitly wrapped in a
 * `logger.error(...)` call reached Loki. An uncaught Vue render error, a rejected
 * promise in a `watch`, or a `TypeError` from a lazily-loaded chunk produced a
 * broken screen for the user and *nothing* in telemetry — which is precisely the
 * class of regression that stays quiet until a user reports it days later.
 *
 * Everything here funnels into the existing pino → OTEL logger (see lib/logger.ts),
 * so the records inherit the resource attributes from instrumentation.ts and the
 * `user.id` / `user.currentOfficeLocationId` enrichment from
 * UserContextLogRecordProcessor. No new transport, no new vendor.
 */

/** Records emitted from here carry this attribute, so alert rules can select them. */
export const GLOBAL_ERROR_MARKER = 'global-error';

/** Where the error was intercepted. */
export type ErrorSource =
  | 'vue-error-handler'
  | 'vue-warn-handler'
  | 'window-error'
  | 'unhandled-rejection';

/**
 * A render loop that throws can emit thousands of identical errors per minute.
 * Unbounded, that floods Loki, buries every other signal, and can wedge the
 * browser on the export queue — so identical errors are collapsed into one record
 * per window, and the session is capped overall.
 */
const DEDUPE_WINDOW_MS = 60_000;
const MAX_RECORDS_PER_SESSION = 100;

/** fingerprint -> when we last emitted it */
const lastEmittedAt = new Map<string, number>();
let emittedCount = 0;
let suppressionNoticeSent = false;

/** Test seam: reset the module-level throttling state. */
export const resetErrorReportingState = () => {
  lastEmittedAt.clear();
  emittedCount = 0;
  suppressionNoticeSent = false;
};

interface SerializedError {
  name?: string;
  message: string;
  stack?: string;
  /** Present for Axios failures: the request that failed and how. */
  http?: {
    method?: string;
    url?: string;
    status?: number;
    statusText?: string;
    /** The backend sets x-trace-id on every response; lets us jump to the Tempo trace. */
    traceId?: string;
  };
}

/**
 * Turn an unknown throwable into flat, loggable fields.
 *
 * Axios errors are enriched with request/status/trace-id — the same treatment
 * `formatBootstrapError` in main.ts gives them — because "something failed" is
 * not actionable while "PUT /treatment-plans/:id returned 500, trace abc123" is.
 */
export const serializeError = (error: unknown): SerializedError => {
  if (isAxiosError(error)) {
    const response = error.response;
    const traceId =
      (response?.headers?.['x-trace-id'] as string | undefined) ??
      (response?.data as { traceId?: string } | undefined)?.traceId;

    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      http: {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: response?.status,
        statusText: response?.statusText,
        traceId,
      },
    };
  }

  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }

  // Thrown non-Errors (strings, plain objects, `undefined` from a rejected
  // promise with no reason) still have to produce something greppable.
  if (typeof error === 'object' && error !== null) {
    try {
      return { message: JSON.stringify(error) };
    } catch {
      // Circular structure — fall through to String().
    }
  }

  return { message: String(error) };
};

/**
 * Group identical errors so the dedupe window has something stable to key on.
 *
 * Uses the first stack frame rather than the whole stack: the same bug reached
 * from two different components should still collapse into one record, and the
 * message alone is too coarse (every "Network Error" would merge).
 */
export const fingerprintError = (source: ErrorSource, error: SerializedError): string => {
  const firstFrame =
    error.stack
      ?.split('\n')
      .map((line) => line.trim())
      .find((line) => line.startsWith('at ')) ?? '';

  return [source, error.name ?? '', error.message, firstFrame].join('|');
};

/**
 * Emit one error record, subject to dedupe and the session cap.
 *
 * @returns whether the record was actually emitted (false = suppressed).
 */
const emit = (
  source: ErrorSource,
  error: unknown,
  extra: Record<string, unknown> = {},
  now = Date.now(),
): boolean => {
  const serialized = serializeError(error);
  const fingerprint = fingerprintError(source, serialized);

  const previous = lastEmittedAt.get(fingerprint);
  if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
    return false;
  }

  if (emittedCount >= MAX_RECORDS_PER_SESSION) {
    // Say so exactly once, so a truncated error stream is never mistaken for a
    // quiet one — the same reason we log dropped work elsewhere.
    if (!suppressionNoticeSent) {
      suppressionNoticeSent = true;
      logger.warn({
        msg: 'Global error reporting suppressed: session cap reached',
        marker: GLOBAL_ERROR_MARKER,
        cap: MAX_RECORDS_PER_SESSION,
      });
    }
    return false;
  }

  lastEmittedAt.set(fingerprint, now);
  emittedCount += 1;

  logger.error({
    msg: `Uncaught error (${source}): ${serialized.message}`,
    marker: GLOBAL_ERROR_MARKER,
    errorSource: source,
    fingerprint,
    errorName: serialized.name,
    errorMessage: serialized.message,
    stack: serialized.stack,
    ...(serialized.http ? { http: serialized.http } : {}),
    // The URL is what makes an error navigable back to a screen. Query strings can
    // carry patient ids, so only the path is recorded.
    path: typeof window === 'undefined' ? undefined : window.location.pathname,
    ...extra,
  });

  return true;
};

/** Exposed for tests; production code goes through the installed handlers. */
export const reportError = emit;

/**
 * Install `app.config.errorHandler`, so uncaught errors in render functions,
 * lifecycle hooks, watchers and event handlers are recorded.
 *
 * Vue swallows these by default (it logs to console and carries on), which is why
 * a broken component can look like "the page is just empty" with clean telemetry.
 */
export const installVueErrorHandler = (app: App): void => {
  const previousHandler = app.config.errorHandler;

  app.config.errorHandler = (error, instance, info) => {
    emit('vue-error-handler', error, {
      // Vue's lifecycle hook name, e.g. 'render' or 'setup function'.
      vueHookInfo: info,
      vueComponent: instance?.$options?.name ?? instance?.$?.type?.__name,
    });

    // Preserve any handler installed before us rather than silently replacing it.
    previousHandler?.(error, instance, info);
  };
};

/**
 * Install `window.onerror` / `unhandledrejection` listeners for everything that
 * never reaches Vue: module-load failures, errors in plain listeners, and
 * rejected promises nobody awaited.
 *
 * Safe to call more than once — the listeners are idempotent per module instance.
 */
let installedWindowHandlers: {
  onError: (event: ErrorEvent) => void;
  onRejection: (event: PromiseRejectionEvent) => void;
} | null = null;

export const installWindowErrorHandlers = (): void => {
  if (typeof window === 'undefined' || installedWindowHandlers) return;

  const onError = (event: ErrorEvent) => {
    // Failed <img>/<script>/<link> loads also fire 'error' on window during the
    // capture phase, but arrive as plain Events with no `error` property. Those
    // are asset problems, not exceptions, and would only add noise here.
    if (!event.error && !event.message) return;

    emit('window-error', event.error ?? event.message, {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    emit('unhandled-rejection', event.reason);
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  installedWindowHandlers = { onError, onRejection };
};

/**
 * Install every global handler. Call `installWindowErrorHandlers` as early as
 * possible in bootstrap (before anything can throw) and pass the app once it
 * exists.
 */
export const installGlobalErrorHandlers = (app?: App): void => {
  installWindowErrorHandlers();
  if (app) installVueErrorHandler(app);
};

/**
 * Remove the window listeners again.
 *
 * Tests need this to genuinely un-register between cases — merely clearing an
 * "installed" flag would leave the old listeners attached and let a
 * double-registration bug hide behind the dedupe window.
 */
export const uninstallWindowErrorHandlers = (): void => {
  if (typeof window === 'undefined' || !installedWindowHandlers) return;

  window.removeEventListener('error', installedWindowHandlers.onError);
  window.removeEventListener('unhandledrejection', installedWindowHandlers.onRejection);
  installedWindowHandlers = null;
};
